import { JOURNEY_TYPE_V2 } from 'lib/flights/flightUtils'
import { Models as FlightModels } from '@luxuryescapes/lib-flights-types'
import { mapFareFamily } from './flightFareFamilyMap'
import { FlightViewTypes } from 'constants/flight'

export function journeyV2Map(
  fare: FlightModels.Search.Fare,
  fareFamilies: FlightModels.Search.IFareFamilies = {},
): App.JourneyV2 {
  const fareFamily = fareFamilies[fare.fareFamiliesRef ?? '']

  return {
    id: fare.id,
    provider: fare.provider as App.FlightProviders,
    name: fare.name,
    fareBasisCode: fare.fareBasisCode,
    fareType: fare.fareType,
    carrier: fare.carrier,
    carrierName: fare.carrierName,
    carrierLogo: fare.carrierLogo,
    displayName: fare.displayName,
    cabin: fare.cabin ?? '',
    bookingInfo: fare.bookingInfo,
    pairedFareId: fare.pairedFareId,
    price: fare.price as any,
    flightGroup: mapFlightGroup(fare.flightGroups[0]),
    currencyCode: fare.currencyCode,
    currencySymbol: fare.currencySymbol,
    isInboundCarrierSameFilter: fare.isInboundCarrierSameFilter,
    itemType: JOURNEY_TYPE_V2,
    links: fare.links,
    fareFamily: fareFamily ? mapFareFamily(fareFamily) : undefined,
    upsellFares: fare.upsellFares,
    overrideFlightTilePriceWithUpsellFarePrice: fare.overrideFlightTilePriceWithUpsellFarePrice,
    baggagePurchaseEnabled: fare.baggagePurchaseEnabled,
  }
}

function mapBaggage(baggage: FlightModels.Search.BaggageOption): App.FlightV2BaggageOption {
  return {
    id: baggage.id,
    quantity: baggage.quantity,
    description: baggage.description,
    carrierCode: baggage.carrierCode,
    amount: baggage.amount,
    pieceDescription: baggage.pieceDescription,
    includedInFare: baggage.includedInFare,
    passengerType: baggage.passengerType,
    currency: baggage.currency,
    providerKey: baggage.providerKey,
  }
}

function mapFlight(flight: FlightModels.Search.Flight): App.FlightV2 {
  const carryOnBaggageOptions = flight.carryOnBaggageOptions.map(mapBaggage)
  const checkedBaggageOptions = flight.checkedBaggageOptions.map(mapBaggage)
  return {
    id: flight.flightUniqueId,
    arrivalAirport: flight.arrivalAirport,
    arrivalAirportName: flight.arrivalAirportName,
    arrivalDateTime: `${flight.arrivalDate} ${flight.arrivalTime}`,
    departureAirport: flight.departureAirport,
    departureAirportName: flight.departureAirportName,
    departureDateTime: `${flight.departingDate} ${flight.departingTime}`,
    departureTerminal: flight.departureTerminal,
    arrivalTerminal: flight.arrivalTerminal,
    duration: {
      hours: flight.flightTime?.hours!,
      minutes: flight.flightTime?.minutes!,
    },
    departureCountry: flight.departureCountry,
    arrivalCountry: flight.arrivalCountry,
    flyTime: flight.flyTime,
    aircraftType: flight.aircraftType,
    capacity: flight.capacity,
    flightNumber: flight.flightNumber,
    marketingAirline: {
      code: flight.marketingAirline.code,
      name: flight.marketingAirline.name,
    },
    operatingAirline: {
      code: flight.operatingAirline.code,
      name: flight.operatingAirline.name,
    },
    fareClass: flight.fareClass,
    flightBookingKey: flight.flightBookingKey,
    seatsSelectionAvailable: flight.seatsSelectionAvailable,
    carryOnBaggageOptions,
    checkedBaggageOptions,
    includedCarryOnBaggage: carryOnBaggageOptions.find(option => option.includedInFare),
    includedCheckedBaggage: checkedBaggageOptions.find(option => option.includedInFare),
    status: flight.status,
    itemType: 'flightV2',
    hasStopover: ((flight.stopoverTime?.hours ?? 0) + (flight.stopoverTime?.minutes ?? 0)) > 0,
    stopoverTime: {
      hours: flight.stopoverTime?.hours ?? 0,
      minutes: flight.stopoverTime?.minutes ?? 0,
    },
    departingTime: flight.departingTime,
    departingDate: flight.departingDate,
    arrivalTime: flight.arrivalTime,
    arrivalDate: flight.arrivalDate,
    bookingClass: flight.bookingClass,
    seatSummary: {
      selectionAvailable: flight.seatSummary.selectionAvailable,
      selectionPolicy: flight.seatSummary.selectionPolicy,
    },
  }
}

function mapFlightGroup(flightGroup: FlightModels.Search.FlightGroup): App.FlightGroup {
  return {
    id: flightGroup.flightGroupId,
    flightGroupNumber: flightGroup.flightGroupNumber,
    flights: flightGroup.flights.map(mapFlight),
    totalStopOverTime: {
      hours: flightGroup.totalStopOverTime?.hours ?? 0,
      minutes: flightGroup.totalStopOverTime?.minutes ?? 0,
    },
    totalTravelDuration: {
      hours: flightGroup.totalTravelDuration.hours,
      minutes: flightGroup.totalTravelDuration.minutes,
    },
    totalFlightDuration: {
      hours: flightGroup.totalFlightDuration.hours,
      minutes: flightGroup.totalFlightDuration.minutes,
    },
    numberOfStops: flightGroup.numberOfStops,
  }
}

export function mapJourneyV1ToV2(
  journey: App.Journey,
): [App.JourneyV2 | undefined, App.JourneyV2 | undefined ] {
  if (!journey) {
    return [undefined, undefined]
  }
  return [
    mapFlightJourneyV1ToV2(journey.id, journey.departing, journey.fareType)!,
    mapFlightJourneyV1ToV2(journey.id, journey.returning, journey.fareType),
  ]
}

function mapFlightJourneyV1ToV2(
  id: string,
  journeyFlight: App.JourneyFlight | undefined,
  fareType: string | undefined,
): App.JourneyV2 | undefined {
  if (!journeyFlight) {
    return
  }
  return {
    id,
    provider: journeyFlight.provider ?? 'amadeus', // A default is required
    name: journeyFlight.carrierName ?? '',
    fareBasisCode: journeyFlight.flights[0]?.fareBasis ?? '',
    fareType: fareType === 'return' ? FlightViewTypes.RETURN : FlightViewTypes.ONE_WAY,
    carrier: journeyFlight.flights[0]?.carrier ?? '',
    carrierName: journeyFlight.carrierName ?? '',
    carrierLogo: journeyFlight.carrierLogo ?? '',
    displayName: journeyFlight.carrierName ?? '',
    cabin: '',
    bookingInfo: {
      bookingClass: journeyFlight.flights[0]?.bookingClass ?? '',
      boookingKey: journeyFlight.flights[0]?.bookingClass ?? '', // Existing type
      journeyKey: journeyFlight.journeyKey,
      providerSearchId: journeyFlight.providerSearchId ?? '',
    },
    pairedFareId: [],
    price: {} as any,
    flightGroup: mapV1ToFlightGroup(journeyFlight),
    currencyCode: '',
    currencySymbol: '',
    isInboundCarrierSameFilter: true,
    itemType: JOURNEY_TYPE_V2,
    links: [],
    fareFamily: undefined,
    upsellFares: [],
    overrideFlightTilePriceWithUpsellFarePrice: false,
    baggagePurchaseEnabled: false,
  }
}

function mapV1ToFlightGroup(journey: App.JourneyFlight): App.FlightGroup {
  return {
    id: '',
    flightGroupNumber: 0,
    flights: journey.extras ? journey.flights.map(mapV1ToFlight(journey, journey.extras)) : [],
    totalStopOverTime: {} as any,
    totalTravelDuration: journey.flightTime ?? {} as any,
    totalFlightDuration: journey.flightTime ?? {} as any,
    numberOfStops: journey.flights.length - 1,
  }
}

function mapV1ToFlight(journey: App.JourneyFlight, addons: App.JourneyAddons) {
  return (flight: App.Flight): App.FlightV2 => {
    const carryOnBaggageOptions = (addons.carryOnBaggage ?? []).map(mapV1ToBaggage(flight.carrier))
    const checkedBaggageOptions = (addons.baggage ?? []).map(mapV1ToBaggage(flight.carrier))
    return {
      id: flight.flightNumber,
      arrivalAirport: flight.arrivalAirport,
      arrivalAirportName: flight.arrivalAirportName,
      arrivalDateTime: `${flight.arrivalDate} ${flight.arrivalTime}`,
      departureAirport: flight.departingAirport,
      departureAirportName: flight.departingAirportName,
      departureDateTime: `${flight.departingDate} ${flight.departingTime}`,
      departureTerminal: '',
      arrivalTerminal: '',
      duration: {
        hours: 0,
        minutes: 0,
      },
      departureCountry: flight.departingCountry,
      arrivalCountry: flight.arrivalCountry,
      flyTime: 0,
      aircraftType: flight.aircraftType,
      capacity: '',
      flightNumber: flight.flightNumber,
      marketingAirline: {
        code: flight.carrier,
        name: flight.carrierName,
      },
      operatingAirline: {
        code: flight.operatingCarrier,
        name: flight.operatedByText ? flight.operatedByText.replace('Operated by ', '') : flight.carrierName,
      },
      fareClass: flight.fareClass,
      flightBookingKey: '',
      seatsSelectionAvailable: '',
      carryOnBaggageOptions,
      checkedBaggageOptions,
      includedCarryOnBaggage: flight.freeCarryOnBaggageDescription ? { description: flight.freeCarryOnBaggageDescription } as App.FlightV2BaggageOption : carryOnBaggageOptions.find(option => option.includedInFare),
      includedCheckedBaggage: flight.freeCheckedInBaggageDescription ? { description: flight.freeCheckedInBaggageDescription } as App.FlightV2BaggageOption : checkedBaggageOptions.find(option => option.includedInFare),
      status: '',
      itemType: 'flightV2',
      hasStopover: flight.layoverTime.hours + flight.layoverTime.minutes > 0,
      stopoverTime: {
        hours: flight.layoverTime.hours,
        minutes: flight.layoverTime.minutes,
      },
      departingTime: flight.departingTime,
      departingDate: flight.departingDate,
      arrivalTime: flight.arrivalTime,
      arrivalDate: flight.arrivalDate,
      bookingClass: flight.bookingClass,
      seatSummary: {} as any,
    }
  }
}

function mapV1ToBaggage(carrierCode: string) {
  return (baggage: App.JourneyAddon): App.FlightV2BaggageOption => {
    return {
      id: baggage.id,
      quantity: baggage.quantity ?? '',
      description: baggage.description,
      carrierCode,
      amount: baggage.amount,
      pieceDescription: '',
      includedInFare: true,
      passengerType: {} as any,
      currency: '',
      providerKey: '',
    }
  }
}
